import { useEffect, useState } from "react";
import { MessageSquare, Phone, Send, Settings, Search, Grid3x3, ChevronRight } from "lucide-react";

const DEMOS = [
  { key: "chat", label: "Chat", icon: MessageSquare, sub: "Riddhi → CRM" },
  { key: "voice", label: "Voice", icon: Phone, sub: "Hindi · MR · EN" },
  { key: "outreach", label: "Outreach", icon: Send, sub: "5-touch cadence" },
  { key: "cms", label: "CMS", icon: Settings, sub: "Non-coder friendly" },
  { key: "seo", label: "SEO", icon: Search, sub: "Page 1 in 90d" },
  { key: "all", label: "All 21", icon: Grid3x3, sub: "One OS" },
];

export default function DemoCarousel() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((i) => (i + 1) % DEMOS.length), 5000);
    return () => clearInterval(id);
  }, []);

  const D = DEMOS[active];
  const Icon = D.icon;

  return (
    <div className="yg-card p-3 sm:p-4 relative overflow-hidden" data-testid="demo-carousel">
      {/* Ambient glow */}
      <div
        aria-hidden
        className="absolute -top-20 -right-20 w-[320px] h-[320px] rounded-full opacity-70"
        style={{ background: "radial-gradient(closest-side, rgba(108,58,237,0.38), transparent 70%)" }}
      />

      {/* Header bar */}
      <div className="flex items-center justify-between mb-3 relative">
        <div className="flex items-center gap-1.5 px-1">
          <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#22C55E]/70" />
        </div>
        <div className="yg-mono text-[10px] tracking-[0.22em] text-[var(--yg-text-dim)] uppercase flex items-center gap-2 min-w-0 flex-1 justify-center">
          <Icon className="w-3.5 h-3.5 text-[var(--yg-violet-light)] shrink-0" />
          <span className="truncate">{D.label}<span className="hidden sm:inline"> · <span className="text-[var(--yg-text-mute)] normal-case tracking-normal">{D.sub}</span></span></span>
        </div>
        <div className="yg-mono text-[10px] tracking-[0.2em] text-[var(--yg-text-dim)] uppercase flex items-center gap-2">
          <span className="yg-dot" /> Live
        </div>
      </div>

      {/* Panel — fixed height so tab switching doesn't shift layout */}
      <div
        key={D.key}
        className="rounded-[12px] border border-[var(--yg-border)] bg-[var(--yg-bg-2)] h-[540px] sm:h-[520px] p-4 sm:p-5 relative overflow-hidden"
      >
        {D.key === "chat" && <ChatDemo />}
        {D.key === "voice" && <VoiceDemo />}
        {D.key === "outreach" && <OutreachDemo />}
        {D.key === "cms" && <CmsDemo />}
        {D.key === "seo" && <SeoDemo />}
        {D.key === "all" && <AllServicesDemo />}
      </div>

      {/* Tab strip */}
      <div className="flex items-center gap-1.5 mt-3 overflow-x-auto no-scrollbar" role="tablist" data-testid="demo-tabs">
        {DEMOS.map((d, i) => {
          const TIcon = d.icon;
          const activeTab = i === active;
          return (
            <button
              key={d.key}
              role="tab"
              aria-selected={activeTab}
              onClick={() => setActive(i)}
              className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-[11.5px] font-semibold tracking-tight transition-all ${
                activeTab
                  ? "bg-[var(--yg-violet)] text-white shadow-[0_8px_20px_-8px_var(--yg-violet-glow)]"
                  : "bg-white/[0.03] text-[var(--yg-text-mute)] border border-[var(--yg-border)] hover:text-white hover:border-[var(--yg-border-strong)]"
              }`}
              data-testid={`demo-tab-${d.key}`}
            >
              <TIcon className="w-3.5 h-3.5" />
              {d.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Chat + CRM (WhatsApp-style) ---------- */
function ChatDemo() {
  const [lang, setLang] = useState("EN");
  const LANGS = { EN: "English", HI: "हिन्दी", MR: "मराठी" };
  const scripts = {
    EN: [
      { s: "u", t: "Hi 👋 Any 3BHK near Baner under ₹2.4 Cr?" },
      { s: "ai", t: "Yes — sharing 2 verified projects + resident reviews now." },
      { s: "u", t: "How's the builder's track record?" },
    ],
    HI: [
      { s: "u", t: "Namaste 👋 Baner me 3BHK 2.4 Cr ke andar?" },
      { s: "ai", t: "Ji! 2 verified project + reviews bhej rahi hoon." },
      { s: "u", t: "Builder ka track record kaisa hai?" },
    ],
    MR: [
      { s: "u", t: "नमस्कार 👋 बाणेर मध्ये ३BHK २.४ Cr मध्ये?" },
      { s: "ai", t: "हो! २ verified projects + reviews पाठवते." },
      { s: "u", t: "Builder चा track record कसा आहे?" },
    ],
  };
  const msgs = scripts[lang];

  return (
    <div className="grid sm:grid-cols-[1fr_0.9fr] gap-3 h-full">
      {/* WhatsApp phone-style chat */}
      <div className="flex flex-col gap-2 min-h-0">
        {/* Tagline banner */}
        <div className="rounded-[10px] border border-[var(--yg-violet)]/35 bg-[var(--yg-violet)]/[0.08] px-3 py-2" data-testid="chat-tagline">
          <div className="text-[11.5px] leading-tight text-white font-medium">An AI human for your business</div>
          <div className="text-[10.5px] leading-tight text-[var(--yg-text-mute)] mt-0.5">Speaks <span className="text-[var(--yg-violet-light)] font-semibold">English · मराठी · हिन्दी</span></div>
        </div>

        {/* Language switcher */}
        <div className="flex items-center gap-1.5" data-testid="chat-lang-switch">
          {Object.entries(LANGS).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setLang(k)}
              className={`px-2.5 py-1 rounded-full text-[10.5px] font-semibold transition-all ${
                lang === k
                  ? "bg-[var(--yg-violet)] text-white shadow-[0_4px_14px_-4px_var(--yg-violet-glow)]"
                  : "bg-white/[0.04] text-[var(--yg-text-mute)] border border-[var(--yg-border)] hover:text-white"
              }`}
              data-testid={`chat-lang-${k.toLowerCase()}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* WhatsApp frame */}
        <div className="yg-wa-frame flex-1 flex flex-col min-h-0" data-testid="whatsapp-mock">
          {/* Header */}
          <div className="yg-wa-header shrink-0">
            <div className="relative w-8 h-8 rounded-full bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center text-white font-bold text-[13px]">R</div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-[12.5px] font-semibold leading-tight truncate">Riddhi · AI Human</div>
              <div className="text-[10px] text-[#25D366] font-medium leading-tight flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#25D366] inline-block" /> online</div>
            </div>
            <div className="flex items-center gap-3 text-[#8696A0] text-[15px]">
              <span aria-hidden>📞</span>
              <span aria-hidden>⋮</span>
            </div>
          </div>

          {/* Body */}
          <div className="yg-wa-body flex-1 flex flex-col gap-1.5 overflow-hidden">
            <div className="text-center my-1">
              <span className="inline-block bg-[#182229] text-[#8696A0] text-[9.5px] px-2 py-0.5 rounded-md">TODAY</span>
            </div>

            <div className="yg-wa-in" style={{ animation: "yg-rise 400ms 0ms both" }}>
              {msgs[0].t}
              <div className="yg-wa-meta"><span>10:24 AM</span></div>
            </div>

            <div className="yg-wa-out" style={{ animation: "yg-rise 400ms 250ms both" }}>
              {msgs[1].t}
              <div className="yg-wa-meta"><span>10:24 AM</span><span className="yg-wa-check">✓✓</span></div>
            </div>

            {/* Rich project card as WhatsApp media message */}
            <div
              className="yg-wa-out !p-0 overflow-hidden"
              style={{ animation: "yg-rise 400ms 500ms both" }}
              data-testid="chat-project-card"
            >
              <div className="h-[68px] bg-gradient-to-br from-[#F59E0B]/70 via-[#6C3AED]/40 to-[#22D3EE]/50 relative flex items-end p-2">
                <span className="absolute top-1.5 right-1.5 bg-black/45 text-white text-[9px] px-1.5 py-0.5 rounded font-semibold">🏗️ 3BHK</span>
                <div className="text-white text-[12.5px] font-bold leading-tight drop-shadow">Kalpataru Vista · Baner</div>
              </div>
              <div className="p-2">
                <div className="text-[11px] text-[#E9EDEF]/95">1420 sqft · Ready · ₹2.28 Cr</div>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-[#F59E0B] text-[11px]">★★★★★</span>
                  <span className="text-[10.5px] text-[#E9EDEF] font-semibold">4.7</span>
                  <span className="text-[10px] text-[#E9EDEF]/70">· 128 reviews</span>
                </div>
                <div className="yg-wa-meta"><span>10:24 AM</span><span className="yg-wa-check">✓✓</span></div>
              </div>
            </div>

            <div className="yg-wa-in" style={{ animation: "yg-rise 400ms 900ms both" }}>
              {msgs[2].t}
              <div className="yg-wa-meta"><span>10:25 AM</span></div>
            </div>

            <div className="flex items-center gap-1.5 mt-0.5" style={{ animation: "yg-rise 400ms 1100ms both" }}>
              <div className="yg-typing"><span /><span /><span /></div>
              <span className="text-[10px] text-[#8696A0]">typing…</span>
            </div>
          </div>

          {/* Compose bar */}
          <div className="shrink-0 flex items-center gap-2 px-2 py-1.5 bg-[#1F2C34] border-t border-white/5">
            <span className="text-[#8696A0] text-[13px]">😊</span>
            <div className="flex-1 bg-[#2A3942] rounded-full px-2.5 py-1 text-[10.5px] text-[#8696A0]">Message</div>
            <div className="w-6 h-6 rounded-full bg-[#25D366] flex items-center justify-center text-white text-[10px]">🎤</div>
          </div>
        </div>
      </div>

      {/* CRM sidebar */}
      <div className="rounded-[12px] border border-[var(--yg-border)] bg-black/30 p-3.5 flex flex-col justify-between min-h-0">
        <div>
          <div className="yg-mono text-[10px] tracking-[0.2em] text-[var(--yg-text-dim)] uppercase mb-2">CRM · Live</div>
          <div className="text-white text-[15px] font-semibold">Rahul M.</div>
          <div className="text-[12px] text-[var(--yg-text-mute)] mt-1">Baner · Pune · 3BHK</div>
          <div className="mt-3 space-y-2 text-[12px]">
            <Row k="Score" v={<span className="text-[var(--yg-green)]">🔥 92</span>} />
            <Row k="Lang" v={LANGS[lang]} />
            <Row k="Sent" v="Vista · Reviews" />
            <Row k="Next" v="Site visit · Sat" />
          </div>
        </div>
        <ProgressRing pct={76} label="Deal fit" />
      </div>
    </div>
  );
}

function Bubble({ children, side = "left", delay = 0, accent = false }) {
  return (
    <div
      className={`flex ${side === "right" ? "justify-end" : "justify-start"}`}
      style={{ animation: `yg-rise 500ms ${delay}ms both cubic-bezier(.2,.9,.3,1)` }}
    >
      <div
        className={`max-w-[86%] px-3.5 py-2.5 text-[13px] leading-snug ${
          accent
            ? "bg-[var(--yg-violet)] text-white rounded-[16px] rounded-br-[6px] shadow-[0_8px_24px_-10px_var(--yg-violet-glow)]"
            : "bg-white/[0.06] text-[var(--yg-text)] border border-[var(--yg-border)] rounded-[16px] rounded-bl-[6px]"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between text-[12px]">
      <span className="text-[var(--yg-text-dim)]">{k}</span>
      <span className="text-white font-medium">{v}</span>
    </div>
  );
}

function ProgressRing({ pct = 60, label = "" }) {
  const r = 22;
  const c = 2 * Math.PI * r;
  const dash = c - (pct / 100) * c;
  return (
    <div className="mt-3 flex items-center gap-3">
      <svg width="56" height="56" viewBox="0 0 56 56" aria-label={`${pct}%`}>
        <circle cx="28" cy="28" r={r} fill="none" strokeWidth="4" className="yg-ring-track" />
        <circle cx="28" cy="28" r={r} fill="none" strokeWidth="4" className="yg-ring-fill"
          strokeDasharray={c} strokeDashoffset={dash} />
      </svg>
      <div>
        <div className="text-white font-semibold text-[18px] tracking-tight">{pct}%</div>
        <div className="yg-mono text-[10px] tracking-[0.2em] text-[var(--yg-text-dim)] uppercase">{label}</div>
      </div>
    </div>
  );
}

/* ---------- Voice ---------- */
function VoiceDemo() {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3">
        <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[var(--yg-violet)] to-[#3B1F8B] flex items-center justify-center shadow-[0_10px_30px_-10px_var(--yg-violet-glow)]">
          <span className="absolute inset-0 rounded-full border border-white/25 yg-slow-spin" style={{ borderStyle: "dashed" }} />
          <span className="text-white font-extrabold text-lg tracking-tight">R</span>
        </div>
        <div>
          <div className="text-white font-semibold text-[15px]">Riddhi · Voice</div>
          <div className="yg-mono text-[10px] tracking-[0.22em] text-[var(--yg-text-dim)] uppercase mt-0.5">HI · MR · EN</div>
        </div>
        <div className="ml-auto yg-wave" aria-hidden><i /><i /><i /><i /><i /><i /><i /></div>
      </div>

      <div className="mt-5 space-y-2.5 text-[13px]">
        <TranscriptLine speaker="Riddhi" delay={0} accent>Namaste! ₹12,500 due hai. Process karein?</TranscriptLine>
        <TranscriptLine speaker="Customer" delay={500}>Kal tak karunga.</TranscriptLine>
        <TranscriptLine speaker="Riddhi" delay={1100} accent>Done! Reminder + WA link bhej rahi hoon. 🙏</TranscriptLine>
        <TranscriptLine speaker="Customer" delay={1700}>Thanks!</TranscriptLine>
      </div>

      <div className="mt-auto pt-4 grid grid-cols-3 gap-2">
        <Metric label="Calls today" value="312" />
        <Metric label="Booked" value="87" />
        <Metric label="Recovery" value="₹4.2L" accent />
      </div>
    </div>
  );
}

function TranscriptLine({ speaker, children, delay = 0, accent = false }) {
  return (
    <div className="flex gap-2.5 items-start" style={{ animation: `yg-rise 500ms ${delay}ms both cubic-bezier(.2,.9,.3,1)` }}>
      <span className={`yg-mono text-[10px] tracking-[0.2em] uppercase mt-0.5 shrink-0 ${accent ? "text-[var(--yg-violet-light)]" : "text-[var(--yg-text-dim)]"}`}>{speaker}</span>
      <span className={accent ? "text-white" : "text-[var(--yg-text-mute)]"}>{children}</span>
    </div>
  );
}

function Metric({ label, value, accent = false }) {
  return (
    <div className="rounded-[10px] border border-[var(--yg-border)] bg-black/30 p-2.5">
      <div className={`text-[16px] font-extrabold tracking-tight ${accent ? "text-[var(--yg-green)]" : "text-white"}`}>{value}</div>
      <div className="yg-mono text-[10px] tracking-[0.2em] text-[var(--yg-text-dim)] uppercase mt-0.5">{label}</div>
    </div>
  );
}

/* ---------- Outreach ---------- */
function OutreachDemo() {
  const steps = [
    { t: "Find prospects", s: "AI scrape · 12,480" },
    { t: "Score + enrich", s: "3,102 qualified" },
    { t: "Personalise", s: "GPT · brand voice" },
    { t: "Multi-channel", s: "Email · WA · LI" },
    { t: "Book meetings", s: "184 this week" },
  ];
  return (
    <div className="h-full flex flex-col">
      <div className="yg-mono text-[10px] tracking-[0.24em] text-[var(--yg-text-dim)] uppercase mb-4">Pipeline · Live</div>
      <div className="space-y-2.5">
        {steps.map((st, i) => (
          <div
            key={st.t}
            className="flex items-center gap-3 rounded-[12px] border border-[var(--yg-border)] bg-white/[0.02] px-3.5 py-2.5"
            style={{ animation: `yg-rise 500ms ${i * 140}ms both cubic-bezier(.2,.9,.3,1)` }}
          >
            <div className="w-7 h-7 rounded-[8px] flex items-center justify-center yg-mono text-[11px] font-bold" style={{ background: `rgba(108,58,237,${0.15 + i * 0.12})`, color: "#DDD5FF" }}>
              0{i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-[13.5px] font-medium truncate">{st.t}</div>
              <div className="text-[11.5px] text-[var(--yg-text-mute)]">{st.s}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-[var(--yg-violet-light)]" />
          </div>
        ))}
      </div>
      <div className="mt-auto pt-4 flex items-center justify-between text-[12px] text-[var(--yg-text-mute)]">
        <span className="yg-mono text-[10px] tracking-[0.22em] uppercase text-[var(--yg-text-dim)]">Reply rate</span>
        <span className="text-white font-bold text-[15px]">18.7% <span className="text-[var(--yg-green)] text-[11px] ml-1">&#9650; 10.2×</span></span>
      </div>
    </div>
  );
}

/* ---------- CMS ---------- */
function CmsDemo() {
  const chips = [
    { i: "🏔️", n: "Trips" },
    { i: "🎬", n: "Hero" },
    { i: "💰", n: "Pricing" },
    { i: "📸", n: "Gallery" },
    { i: "🔍", n: "SEO" },
    { i: "🪟", n: "Popups" },
    { i: "⭐", n: "Reviews" },
  ];
  return (
    <div className="h-full flex flex-col">
      <div className="yg-mono text-[10px] tracking-[0.24em] text-[var(--yg-text-dim)] uppercase mb-3">CMS · Awaiting Adventures</div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {chips.map((c, i) => (
          <span key={c.n} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] bg-white/[0.04] border border-[var(--yg-border)] text-[var(--yg-text-mute)]"
            style={{ animation: `yg-rise 400ms ${i * 60}ms both` }}>
            <span>{c.i}</span>{c.n}
          </span>
        ))}
      </div>

      <div className="rounded-[12px] border border-[var(--yg-border)] bg-black/30 p-3.5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[12px] text-[var(--yg-text-mute)]">Editing</div>
          <div className="text-[10px] yg-mono tracking-[0.2em] uppercase text-[var(--yg-green)] flex items-center gap-1"><span className="yg-dot" /> Live</div>
        </div>
        <div className="space-y-2.5">
          <FieldRow label="Trip Name" value="Ladakh — Ride of a Lifetime" />
          <FieldRow label="Price" value="₹24,999 · 7D/6N" />
          <FieldRow label="Status" value={<span className="inline-flex items-center gap-1.5 text-[11.5px]"><span className="w-2 h-2 rounded-full bg-[var(--yg-green)]" /> Live</span>} />
        </div>
        <div className="mt-4 rounded-[10px] border border-[var(--yg-border)] bg-black/40 p-3">
          <div className="yg-mono text-[10px] tracking-[0.22em] uppercase text-[var(--yg-text-dim)] mb-2">Preview</div>
          <div className="flex items-center gap-2.5">
            <span className="text-[22px]">🏔️</span>
            <div>
              <div className="text-white text-[13.5px] font-semibold">Ladakh — Ride of a Lifetime</div>
              <div className="text-[11.5px] text-[var(--yg-text-mute)]">₹24,999 · 7D/6N · Live</div>
            </div>
          </div>
        </div>
      </div>

      <button className="mt-auto self-end yg-btn yg-btn-primary text-[12px] py-2 px-4 mt-4">Save & Publish</button>
    </div>
  );
}

function FieldRow({ label, value }) {
  return (
    <div className="grid grid-cols-[100px_1fr] gap-2 items-center">
      <div className="yg-mono text-[10px] tracking-[0.18em] uppercase text-[var(--yg-text-dim)]">{label}</div>
      <div className="text-white text-[12.5px] font-medium bg-white/[0.03] border border-[var(--yg-border)] rounded-[8px] px-2.5 py-1.5">{value}</div>
    </div>
  );
}

/* ---------- SEO ---------- */
function SeoDemo() {
  return (
    <div className="h-full flex flex-col">
      <div className="yg-mono text-[10px] tracking-[0.24em] text-[var(--yg-text-dim)] uppercase mb-3">SEO Engine · Weekly AI content</div>
      <div className="rounded-[10px] border border-[var(--yg-border)] bg-black/30 p-2.5 flex items-center gap-2 yg-mono text-[11px] text-[var(--yg-text-mute)]">
        <span className="text-[var(--yg-violet-light)]">google.com/search?q=</span>
        <span className="text-white">best wellness clinic pune</span>
        <span className="yg-cursor" />
      </div>

      <div className="mt-3 rounded-[12px] border border-[var(--yg-border)] bg-black/40 p-3.5" style={{ animation: "yg-rise 500ms 300ms both" }}>
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] yg-mono tracking-[0.2em] uppercase text-[var(--yg-green)] px-2 py-0.5 rounded-full border border-[var(--yg-green)]/40">#1 Result</span>
          <span className="yg-mono text-[10.5px] text-[var(--yg-text-dim)]">yourwellnessclinic.com</span>
        </div>
        <div className="text-[15px] text-white font-semibold tracking-[-0.01em]">Best Wellness Clinic in Pune — Verified 2026</div>
        <div className="text-[12px] text-[var(--yg-text-mute)] mt-1 leading-relaxed">Book online in 30s. Ayurveda + physio + skin. 4.9★ · 87 reviews · Kothrud & Baner.</div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <StatChip big="12" small="Keywords" />
        <StatChip big="90d" small="→ Page 1" />
        <StatChip big="4×" small="Traffic" accent />
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-1.5 text-[11px]">
          <span className="yg-mono tracking-[0.2em] uppercase text-[var(--yg-text-dim)]">Ranking</span>
          <span className="text-white font-semibold">Position 1 · rising</span>
        </div>
        <div className="h-2 rounded-full bg-white/[0.05] overflow-hidden">
          <div className="h-full yg-shimmer yg-grow-x rounded-full" style={{ width: "92%", background: "linear-gradient(90deg, var(--yg-violet), var(--yg-violet-light))" }} />
        </div>
      </div>
    </div>
  );
}

function StatChip({ big, small, accent = false }) {
  return (
    <div className="rounded-[10px] border border-[var(--yg-border)] bg-black/30 p-2.5 text-center">
      <div className={`text-[18px] font-extrabold tracking-tight ${accent ? "text-[var(--yg-green)]" : "text-white"}`}>{big}</div>
      <div className="yg-mono text-[9.5px] tracking-[0.18em] text-[var(--yg-text-dim)] uppercase mt-0.5">{small}</div>
    </div>
  );
}

/* ---------- All Services ---------- */
function AllServicesDemo() {
  const items = [
    { i: "🗣️", n: "AI Human", m: "3 langs", accent: true },
    { i: "💬", n: "Chatbot", m: "80% auto" },
    { i: "📞", n: "Voice", m: "3 langs" },
    { i: "🎯", n: "Lead AI", m: "-60% waste" },
    { i: "✍️", n: "Content", m: "3× output" },
    { i: "📊", n: "Meta Ads", m: "+20% ROI" },
    { i: "💚", n: "WhatsApp", m: "5× opens" },
    { i: "📅", n: "Booking", m: "-35% NS" },
    { i: "⭐", n: "Reviews", m: "+9% rev" },
    { i: "🗺️", n: "Itinerary", m: "AI-built" },
    { i: "🔄", n: "CRM", m: "3h saved" },
    { i: "📈", n: "Strategy", m: "Data + AI" },
    { i: "💳", n: "Payments", m: "-45% due" },
    { i: "🌐", n: "Website", m: "1 day" },
    { i: "📱", n: "App", m: "iOS + And" },
    { i: "🔍", n: "SEO", m: "Page 1" },
    { i: "📧", n: "Email", m: "Auto seq" },
    { i: "👥", n: "HR AI", m: "-50% time" },
    { i: "💰", n: "Finance", m: "Auto-rec" },
    { i: "⚙️", n: "CMS", m: "No-code" },
    { i: "🛡️", n: "Ops AI", m: "3× retain" },
  ];
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-2.5 shrink-0">
        <div className="yg-mono text-[10px] tracking-[0.24em] text-[var(--yg-text-dim)] uppercase">21 Services · One OS</div>
        <div className="yg-mono text-[10px] tracking-[0.22em] uppercase text-[var(--yg-green)] flex items-center gap-1.5"><span className="yg-dot" /> Live</div>
      </div>

      <div className="grid grid-cols-3 gap-1.5 flex-1 min-h-0">
        {items.map((it, i) => (
          <div
            key={it.n + i}
            className={`rounded-[9px] transition-all p-1.5 sm:p-2 flex flex-col justify-center items-start gap-0.5 min-w-0 min-h-0 ${
              it.accent
                ? "bg-[var(--yg-violet)]/18 border border-[var(--yg-violet)]/55"
                : "bg-white/[0.03] border border-[var(--yg-border)] hover:bg-[var(--yg-violet)]/10 hover:border-[var(--yg-violet)]/40"
            }`}
            style={{ animation: `yg-rise 400ms ${(i * 30)}ms both cubic-bezier(.2,.9,.3,1)` }}
          >
            <span className="text-[13px] leading-none">{it.i}</span>
            <div className="text-white text-[10.5px] font-semibold leading-[1.1] break-words w-full">{it.n}</div>
            <div className={`yg-mono text-[8.5px] leading-[1.1] tracking-tight break-words w-full ${it.accent ? "text-white/85" : "text-[var(--yg-violet-light)]"}`}>{it.m}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
